/* eslint-disable */
import React, {useEffect, useState} from 'react';
import {useForm, useWatch, Controller} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import * as yup from 'yup';
import moment from 'moment';
import {useNavigate, useParams} from 'react-router-dom';
import toast from 'react-hot-toast';
import {IoMdArrowBack} from 'react-icons/io';
import {DatePicker} from 'antd';
import dayjs from 'dayjs';
import 'antd/dist/reset.css';

import {useGetSubjectListQuery} from '../../api/candidateTest';
import {useGetCodingQuestionQuery} from '../../api/codingChallenge';
import {
  useCreateAssessmentMutation,
  useUpdateAssessmentMutation,
  useGetAssessmentByIdQuery,
} from '../../api/assessment';
import Button from '../../components/ui/Button';
import InputField from '../../components/ui/InputField';
import {SelectFieldHookForm} from '../../components/ui/SelectField';
import {pageRoutes} from '../../constants/pageRoutes';
import useAuthStore from '../../store/useAuthStore';

const createAssessmentSchema = (isEditMode = false) =>
  yup
    .object()
    .shape({
      name: yup.string().required('Assessment name is required'),
      slug: yup
        .string()
        .required('Slug is required')
        .matches(/^[^\s]+$/, 'Slug should not contain spaces'),
      startDateTime: yup
        .string()
        .required('Start date and time is required')
        .test('is-date', 'Please enter a valid date and time', (value) => {
          if (!value) return false;
          const date = new Date(value);
          return !isNaN(date.getTime());
        })
        .test(
          'is-future',
          'Start date and time must be in the future',
          (value) => {
            if (isEditMode || !value) return true; // Skip validation for edit mode
            const date = new Date(value);
            return date > new Date();
          },
        ),
      endDateTime: yup
        .string()
        .required('End date and time is required')
        .test('is-date', 'Please enter a valid date and time', (value) => {
          if (!value) return false;
          const date = new Date(value);
          return !isNaN(date.getTime());
        })
        .test(
          'is-after-start',
          'End date must be after start date',
          function (value) {
            if (!value) return true;
            const startDate = this.parent.startDateTime;
            if (!startDate) return true;

            const endDate = new Date(value);
            const start = new Date(startDate);

            if (isNaN(endDate.getTime()) || isNaN(start.getTime())) return true;
            return endDate > start;
          },
        ),
      collegeName: yup.string(),
      tests: yup
        .array()
        .nullable()
        .transform((value) => (!value ? [] : value)),
      codingChallengeType: yup
        .string()
        .oneOf(
          ['none', 'fixed', 'random'],
          'Please select a valid coding challenge type',
        )
        .required('Please select a coding challenge option'),
      codingProblems: yup
        .array()
        .nullable()
        .transform((value) => (!value ? [] : value))
        .when('codingChallengeType', {
          is: 'fixed',
          then: yup
            .array()
            .min(
              1,
              'Please select at least one coding problem when using fixed coding challenges',
            ),
          otherwise: yup.array(),
        }),
      totalChallenges: yup.number().when('codingChallengeType', {
        is: 'random',
        then: yup.number().min(0),
        otherwise: yup.number().min(0),
      }),
      queryCount: yup
        .number()
        .typeError('Query Count must be 0 or more')
        .transform((value, originalValue) => {
          return originalValue === '' ? 0 : value;
        })
        .when('codingChallengeType', {
          is: 'random',
          then: yup
            .number()
            .min(0, 'Query Count must be 0 or more')
            .required(
              'Query Count is required when random assignment is enabled',
            ),
          otherwise: yup.number().min(0, 'Query Count must be 0 or more'),
        }),
      nonQueryCount: yup
        .number()
        .typeError('Non-Query Count must be 0 or more')
        .transform((value, originalValue) => {
          return originalValue === '' ? 0 : value;
        })
        .when('codingChallengeType', {
          is: 'random',
          then: yup
            .number()
            .min(0, 'Non-Query Count must be 0 or more')
            .required(
              'Non-Query Count is required when random assignment is enabled',
            ),
          otherwise: yup.number().min(0, 'Non-Query Count must be 0 or more'),
        }),
      problemPool: yup.array().when('codingChallengeType', {
        is: 'random',
        then: yup
          .array()
          .min(
            1,
            'Problem pool must contain at least 1 problem when random assignment is enabled',
          )
          .required(
            'Problem pool is required when random assignment is enabled',
          ),
        otherwise: yup.array(),
      }),
    })
    .test(
      'at-least-one-test-or-coding',
      'Please select at least one test or coding challenge',
      function (values) {
        const tests = values.tests || [];
        const codingChallengeType = values.codingChallengeType;
        const codingProblems = values.codingProblems || [];
        const queryCount = values.queryCount || 0;
        const nonQueryCount = values.nonQueryCount || 0;
        const calculatedTotal = queryCount + nonQueryCount;

        const hasCodingChallenges =
          (codingChallengeType === 'fixed' && codingProblems.length > 0) ||
          (codingChallengeType === 'random' && calculatedTotal > 0);

        if (tests.length === 0 && !hasCodingChallenges) {
          return this.createError({
            path: 'tests',
            message: 'Please select at least one test or coding challenge',
          });
        }

        return true;
      },
    )
    .test(
      'coding-config-validation',
      'Invalid coding configuration',
      function (values) {
        const codingChallengeType = values.codingChallengeType;

        if (codingChallengeType === 'random') {
          const {queryCount, nonQueryCount} = values;
          const totalChallenges = (queryCount || 0) + (nonQueryCount || 0);

          if (totalChallenges === 0) {
            return this.createError({
              path: 'queryCount',
              message:
                'At least one challenge (query or non-query) must be specified',
            });
          }
        }

        return true;
      },
    );

const AssessmentForm = () => {
  const navigate = useNavigate();
  const {id} = useParams();
  const user = useAuthStore((state) => state.user);
  const isInvigilator = user?.role === 'invigilator';
  const isEditMode = !!id;
  const [initialValues, setInitialValues] = useState({});

  const {
    register,
    handleSubmit,
    control,
    formState: {errors},
    reset,
    watch,
  } = useForm({
    resolver: yupResolver(createAssessmentSchema(isEditMode)),
    mode: 'onChange',
    defaultValues: {
      codingChallengeType: 'none',
      queryCount: 0,
      nonQueryCount: 0,
      problemPool: [],
    },
  });

  // Watch the codingChallengeType field to show/hide appropriate fields
  const codingChallengeType = useWatch({
    control,
    name: 'codingChallengeType',
    defaultValue: 'none',
  });

  // Watch query and non-query counts to calculate total automatically
  const queryCount = useWatch({
    control,
    name: 'queryCount',
    defaultValue: 0,
  });

  const nonQueryCount = useWatch({
    control,
    name: 'nonQueryCount',
    defaultValue: 0,
  });

  const calculatedTotal =
    (Number(queryCount) || 0) + (Number(nonQueryCount) || 0);

  // Fetch assessment data if editing
  const {
    data: assessmentData,
    isLoading: isLoadingAssessment,
    error: assessmentError,
  } = useGetAssessmentByIdQuery(id, {
    enabled: isEditMode && !!id,
  });

  // Mutations for CRUD operations
  const {mutate: createAssessmentMutate, isLoading: isCreateLoading} =
    useCreateAssessmentMutation();
  const {mutate: updateAssessmentMutate, isLoading: isUpdateLoading} =
    useUpdateAssessmentMutation();

  // Fetch test subjects
  const {data: testSubjects, isLoading: isTestsLoading} =
    useGetSubjectListQuery();
  const testArray = Array.isArray(testSubjects)
    ? testSubjects
    : Array.isArray(testSubjects?.data)
    ? testSubjects.data
    : [];
  const testOptions = testArray.map((t) => ({
    value: t._id,
    label: t.subject,
  }));

  // Fetch coding problems
  const {data: codingProblems, isLoading: isCodingProblemsLoading} =
    useGetCodingQuestionQuery({limit: 1000});
  const codingArray = Array.isArray(codingProblems?.data?.problemData)
    ? codingProblems.data.problemData
    : [];
  const codingOptions = codingArray.map((p) => ({
    value: p._id,
    label: `${p.name} (${p.subjects?.map((s) => s.subject).join(', ')}) - ${
      p.is_this_query ? 'Query' : 'Non-Query'
    }`,
  }));

  // Set initial values for edit mode
  useEffect(() => {
    if (isEditMode && assessmentData?.data) {
      const assessment = assessmentData.data;

      // Determine coding challenge type based on existing data
      let codingChallengeType = 'none';
      if (assessment.codingConfig?.enableRandomAssignment) {
        codingChallengeType = 'random';
      } else if (
        assessment.codingProblems &&
        assessment.codingProblems.length > 0
      ) {
        codingChallengeType = 'fixed';
      }

      const formattedValues = {
        ...assessment,
        startDateTime: assessment.startDateTime
          ? moment
              .utc(assessment.startDateTime)
              .local()
              .format('YYYY-MM-DDTHH:mm')
          : '',
        endDateTime: assessment.endDateTime
          ? moment
              .utc(assessment.endDateTime)
              .local()
              .format('YYYY-MM-DDTHH:mm')
          : '',
        tests:
          assessment.tests?.map((test) =>
            typeof test === 'object'
              ? {value: test._id, label: test.subject}
              : test,
          ) || [],
        codingChallengeType,
        codingProblems:
          assessment.codingProblems?.map((problem) =>
            typeof problem === 'object'
              ? {value: problem._id, label: problem.name}
              : problem,
          ) || [],
        queryCount: assessment.codingConfig?.queryCount || 0,
        nonQueryCount: assessment.codingConfig?.nonQueryCount || 0,
        problemPool:
          assessment.codingConfig?.problemPool?.map((id) => ({
            value: id,
            label: codingArray.find((p) => p._id === id)?.name || id,
          })) || [],
      };

      setInitialValues(formattedValues);
      reset(formattedValues);
    }
  }, [assessmentData, isEditMode, reset, codingArray]);

  const handleFormSubmission = (values) => {
    const formData = {...values};

    // Transform date strings to ISO strings
    if (formData.startDateTime) {
      const startDate = new Date(formData.startDateTime);
      if (!isNaN(startDate.getTime())) {
        formData.startDateTime = startDate.toISOString();
      }
    }

    if (formData.endDateTime) {
      const endDate = new Date(formData.endDateTime);
      if (!isNaN(endDate.getTime())) {
        formData.endDateTime = endDate.toISOString();
      }
    }

    // Transform arrays to plain ObjectIds
    if (formData.tests && Array.isArray(formData.tests)) {
      formData.tests = formData.tests.map((test) => {
        if (typeof test === 'object') {
          return test.value || test._id;
        }
        return test;
      });

      // If tests array is empty, remove it from the payload
      if (formData.tests.length === 0) {
        delete formData.tests;
      }
    }

    // Handle coding challenges based on type
    if (formData.codingChallengeType === 'fixed') {
      // Keep codingProblems and set random assignment to false
      if (formData.codingProblems && Array.isArray(formData.codingProblems)) {
        formData.codingProblems = formData.codingProblems.map((problem) => {
          if (typeof problem === 'object') {
            return problem.value || problem._id;
          }
          return problem;
        });
      }
      formData.codingConfig = {
        enableRandomAssignment: false,
        totalChallenges: 0,
        queryCount: 0,
        nonQueryCount: 0,
        problemPool: [],
      };
    } else if (formData.codingChallengeType === 'random') {
      // Clear fixed coding problems and set up random assignment
      formData.codingProblems = [];
      const totalChallenges =
        (formData.queryCount || 0) + (formData.nonQueryCount || 0);
      formData.codingConfig = {
        enableRandomAssignment: true,
        totalChallenges: totalChallenges,
        queryCount: formData.queryCount || 0,
        nonQueryCount: formData.nonQueryCount || 0,
        problemPool: [],
      };

      if (formData.problemPool && Array.isArray(formData.problemPool)) {
        formData.codingConfig.problemPool = formData.problemPool.map(
          (problem) => {
            if (typeof problem === 'object') {
              return problem.value || problem._id;
            }
            return problem;
          },
        );
      }
    } else {
      // None - clear both coding problems and random assignment
      formData.codingProblems = [];
      formData.codingConfig = {
        enableRandomAssignment: false,
        totalChallenges: 0,
        queryCount: 0,
        nonQueryCount: 0,
        problemPool: [],
      };
    }

    // Remove the top-level coding config fields
    delete formData.codingChallengeType;
    delete formData.totalChallenges;
    delete formData.queryCount;
    delete formData.nonQueryCount;
    delete formData.problemPool;

    const onSuccess = (message) => {
      toast.success(message);
      navigate(pageRoutes.assessments);
    };

    const onError = (error) => {
      toast.error(error.response?.data?.message || 'An error occurred');
    };

    if (isEditMode) {
      updateAssessmentMutate(
        {id, ...formData},
        {
          onSuccess: () => onSuccess('Assessment updated successfully'),
          onError,
        },
      );
    } else {
      createAssessmentMutate(formData, {
        onSuccess: () => onSuccess('Assessment created successfully'),
        onError,
      });
    }
  };

  const handleCancel = () => {
    navigate(pageRoutes.assessments);
  };

  if (isEditMode && isLoadingAssessment) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading assessment...</p>
        </div>
      </div>
    );
  }

  if (isEditMode && assessmentError) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <p className="text-red-600">Error loading assessment</p>
          <Button onClick={handleCancel} variant="outlined" className="mt-4">
            Back to Assessments
          </Button>
        </div>
      </div>
    );
  }

  const isLoading = isCreateLoading || isUpdateLoading;

  // Custom styles for DatePicker
  const datePickerStyles = {
    container: {
      width: '100%',
      height: '48px',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: '500',
      transition: 'all 0.2s ease',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    },
    error: {
      border: '2px solid #ef4444',
      boxShadow: '0 0 0 3px rgba(239, 68, 68, 0.1)',
    },
    normal: {
      border: '1px solid #d1d5db',
      '&:hover': {
        borderColor: '#3b82f6',
        boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
      },
      '&:focus-within': {
        borderColor: '#3b82f6',
        boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
      },
    },
  };

  // Custom CSS for DatePicker theming
  const datePickerCSS = `
    .custom-datepicker .ant-picker-dropdown {
      border-radius: 6px !important;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
      border: 1px solid #e5e7eb !important;
    }
    
    .custom-datepicker .ant-picker-panel {
      border: none !important;
      border-radius: 6px !important;
    }
    
    .custom-datepicker .ant-picker-header {
      border-bottom: 1px solid #e5e7eb !important;
      padding: 12px 16px !important;
      background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%) !important;
      border-radius: 6px 6px 0 0 !important;
    }
    
    .custom-datepicker .ant-picker-header-view {
      color: white !important;
      font-weight: 600 !important;
      font-size: 15px !important;
    }
    
    .custom-datepicker .ant-picker-prev-icon,
    .custom-datepicker .ant-picker-next-icon,
    .custom-datepicker .ant-picker-super-prev-icon,
    .custom-datepicker .ant-picker-super-next-icon {
      color: white !important;
    }
    
    .custom-datepicker .ant-picker-content {
      padding: 8px !important;
    }
    
    .custom-datepicker .ant-picker-cell {
      padding: 2px !important;
    }
    
    .custom-datepicker .ant-picker-cell-inner {
      border-radius: 4px !important;
      font-weight: 500 !important;
      transition: all 0.15s ease !important;
      height: 28px !important;
      line-height: 28px !important;
      margin: 1px !important;
    }
    
    .custom-datepicker .ant-picker-cell:hover .ant-picker-cell-inner {
      background: #eff6ff !important;
      color: #1d4ed8 !important;
    }
    
    .custom-datepicker .ant-picker-cell-selected .ant-picker-cell-inner {
      background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%) !important;
      color: white !important;
      font-weight: 600 !important;
    }
    
    .custom-datepicker .ant-picker-cell-today .ant-picker-cell-inner {
      border: 1px solid #3b82f6 !important;
      font-weight: 600 !important;
    }
    
    .custom-datepicker .ant-picker-time-panel {
      border-left: 1px solid #e5e7eb !important;
    }
    
    .custom-datepicker .ant-picker-time-panel-cell:hover {
      background: #eff6ff !important;
      color: #1d4ed8 !important;
    }
    
    .custom-datepicker .ant-picker-time-panel-cell-selected {
      background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%) !important;
      color: white !important;
      font-weight: 600 !important;
    }
    
    .custom-datepicker .ant-picker-now-btn {
      color: #3b82f6 !important;
      font-weight: 600 !important;
      border-radius: 4px !important;
      padding: 4px 8px !important;
      transition: all 0.15s ease !important;
      font-size: 12px !important;
    }
    
    .custom-datepicker .ant-picker-now-btn:hover {
      background: #eff6ff !important;
      color: #1d4ed8 !important;
    }
    
    .custom-datepicker .ant-picker-ok {
      background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%) !important;
      border: none !important;
      border-radius: 4px !important;
      font-weight: 600 !important;
      padding: 6px 16px !important;
      height: auto !important;
      transition: all 0.15s ease !important;
      font-size: 12px !important;
    }
    
    .custom-datepicker .ant-picker-ok:hover {
      opacity: 0.9 !important;
    }
    
    .custom-datepicker .ant-picker-footer {
      padding: 6px 8px !important;
      border-top: 1px solid #e5e7eb !important;
      background: #fafbfc !important;
      border-radius: 0 0 6px 6px !important;
    }
  `;

  // Add CSS to head
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = datePickerCSS;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-8 py-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="text-2xl font-bold text-gray-900">
              {isEditMode
                ? `Edit Assessment - ${initialValues.name || ''}`
                : 'Create New Assessment'}
            </h1>
          </div>
          <button
            onClick={handleCancel}
            className="flex items-center text-gray-600 hover:text-gray-800 gap-2"
          >
            <IoMdArrowBack size={20} />
            <span>Back to Assessments</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-8 py-8">
        <div className=" mx-auto">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-8 py-6">
              <form onSubmit={handleSubmit(handleFormSubmission)}>
                <div className="space-y-8">
                  {/* Basic Information Section */}
                  <div className="space-y-6">
                    {/* Row 1: Assessment Name | Slug */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Assessment Name *
                        </label>
                        <InputField
                          {...register('name')}
                          placeholder="Enter assessment name"
                          error={errors.name?.message}
                          className="w-full"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Slug *
                        </label>
                        <InputField
                          {...register('slug')}
                          placeholder="Enter slug"
                          error={errors.slug?.message}
                          className="w-full"
                        />
                      </div>
                    </div>

                    {/* Row 2: Start Date and Time | End Date and Time */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Start Date and Time *
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                            <svg
                              className="w-5 h-5 text-gray-400"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                          <Controller
                            control={control}
                            name="startDateTime"
                            render={({field: {onChange, value}}) => (
                              <DatePicker
                                showTime={{
                                  format: 'h:mm A',
                                  use12Hours: true,
                                  minuteStep: 15,
                                }}
                                format="MMMM D, YYYY h:mm A"
                                placeholder="Select start date and time"
                                value={value ? dayjs(value) : null}
                                onChange={(date) => {
                                  onChange(
                                    date ? date.format('YYYY-MM-DDTHH:mm') : '',
                                  );
                                }}
                                disabledDate={(current) => {
                                  return (
                                    !isEditMode &&
                                    current &&
                                    current < dayjs().startOf('day')
                                  );
                                }}
                                style={{
                                  ...datePickerStyles.container,
                                  paddingLeft: '40px',
                                  border: errors.startDateTime?.message
                                    ? '2px solid #ef4444'
                                    : '1px solid #d1d5db',
                                }}
                                popupClassName="custom-datepicker"
                                popupStyle={{
                                  zIndex: 10000,
                                }}
                              />
                            )}
                          />
                        </div>
                        {errors.startDateTime?.message && (
                          <p className="text-red-500 text-sm mt-1 flex items-center">
                            <svg
                              className="w-4 h-4 mr-1"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                                clipRule="evenodd"
                              />
                            </svg>
                            {errors.startDateTime.message}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          End Date and Time *
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                            <svg
                              className="w-5 h-5 text-gray-400"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                          <Controller
                            control={control}
                            name="endDateTime"
                            render={({field: {onChange, value}}) => {
                              const startDateTime = watch('startDateTime');
                              const minDate = startDateTime
                                ? dayjs(startDateTime)
                                : dayjs();

                              return (
                                <DatePicker
                                  showTime={{
                                    format: 'h:mm A',
                                    use12Hours: true,
                                    minuteStep: 15,
                                  }}
                                  format="MMMM D, YYYY h:mm A"
                                  placeholder="Select end date and time"
                                  value={value ? dayjs(value) : null}
                                  onChange={(date) => {
                                    onChange(
                                      date
                                        ? date.format('YYYY-MM-DDTHH:mm')
                                        : '',
                                    );
                                  }}
                                  disabledDate={(current) => {
                                    return (
                                      current &&
                                      current < minDate.startOf('day')
                                    );
                                  }}
                                  disabledTime={(current) => {
                                    if (!current || !startDateTime) return {};

                                    const startDate = dayjs(startDateTime);
                                    if (current.isSame(startDate, 'day')) {
                                      return {
                                        disabledHours: () => {
                                          const hours = [];
                                          for (
                                            let i = 0;
                                            i < startDate.hour();
                                            i++
                                          ) {
                                            hours.push(i);
                                          }
                                          return hours;
                                        },
                                        disabledMinutes: (hour) => {
                                          if (hour === startDate.hour()) {
                                            const minutes = [];
                                            for (
                                              let i = 0;
                                              i <= startDate.minute();
                                              i++
                                            ) {
                                              minutes.push(i);
                                            }
                                            return minutes;
                                          }
                                          return [];
                                        },
                                      };
                                    }
                                    return {};
                                  }}
                                  style={{
                                    ...datePickerStyles.container,
                                    paddingLeft: '40px',
                                    border: errors.endDateTime?.message
                                      ? '2px solid #ef4444'
                                      : '1px solid #d1d5db',
                                  }}
                                  popupClassName="custom-datepicker"
                                  popupStyle={{
                                    zIndex: 10000,
                                  }}
                                />
                              );
                            }}
                          />
                        </div>
                        {errors.endDateTime?.message && (
                          <p className="text-red-500 text-sm mt-1 flex items-center">
                            <svg
                              className="w-4 h-4 mr-1"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                                clipRule="evenodd"
                              />
                            </svg>
                            {errors.endDateTime.message}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Row 3: College Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        College Name
                      </label>
                      <InputField
                        {...register('collegeName')}
                        placeholder="Enter college name"
                        error={errors.collegeName?.message}
                        className="w-full"
                      />
                    </div>
                  </div>

                  {/* Test Configuration Section */}
                  <div className="space-y-6">
                    <div className="border-b border-gray-200 pb-4">
                      <h2 className="text-lg font-medium text-gray-900">
                        Test Configuration
                      </h2>
                      <p className="text-sm text-gray-600 mt-1">
                        Configure the tests and coding challenges for this
                        assessment
                      </p>
                    </div>

                    {/* MCQ Tests */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        MCQ Tests
                      </label>
                      <SelectFieldHookForm
                        control={control}
                        name="tests"
                        options={testOptions}
                        isMulti
                        placeholder={
                          isTestsLoading ? 'Loading tests...' : 'Select tests'
                        }
                        error={errors.tests?.message}
                        className="w-full"
                        isLoading={isTestsLoading}
                      />
                    </div>

                    {/* Coding Challenges Section */}
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                          Coding Challenges
                        </label>
                        {errors.codingChallengeType && (
                          <p className="text-red-600 text-sm mb-3">
                            {errors.codingChallengeType.message}
                          </p>
                        )}

                        {/* Radio Button Options */}
                        <div className="gap-4 flex flex-wrap items-center">
                          {/* None Option */}
                          <div className="relative">
                            <div className="flex items-center">
                              <input
                                {...register('codingChallengeType')}
                                type="radio"
                                value="none"
                                id="coding-none"
                                className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                              />
                              <label
                                htmlFor="coding-none"
                                className="ml-3 block"
                              >
                                <div className="text-sm font-medium text-gray-900">
                                  No Coding Challenges
                                </div>
                                <div className="text-sm text-gray-500">
                                  Skip coding challenges for this assessment
                                </div>
                              </label>
                            </div>
                          </div>

                          {/* Fixed Option */}
                          <div className="relative">
                            <div className="flex items-center">
                              <input
                                {...register('codingChallengeType')}
                                type="radio"
                                value="fixed"
                                id="coding-fixed"
                                className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                              />
                              <label
                                htmlFor="coding-fixed"
                                className="ml-3 block"
                              >
                                <div className="text-sm font-medium text-gray-900">
                                  Fixed Coding Problems
                                </div>
                                <div className="text-sm text-gray-500">
                                  Choose specific coding problems for all
                                  candidates
                                </div>
                              </label>
                            </div>
                          </div>

                          {/* Random Option */}
                          <div className="relative">
                            <div className="flex items-center">
                              <input
                                {...register('codingChallengeType')}
                                type="radio"
                                value="random"
                                id="coding-random"
                                className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                              />
                              <label
                                htmlFor="coding-random"
                                className="ml-3 block"
                              >
                                <div className="text-sm font-medium text-gray-900">
                                  Random Assignment
                                </div>
                                <div className="text-sm text-gray-500">
                                  Randomly assign coding problems to each
                                  candidate
                                </div>
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Fixed Coding Problems Configuration */}
                      {codingChallengeType === 'fixed' && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-4">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                            <h3 className="text-sm font-medium text-green-900">
                              Fixed Coding Problems
                            </h3>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Select Coding Problems *
                            </label>
                            <SelectFieldHookForm
                              control={control}
                              name="codingProblems"
                              options={codingOptions}
                              isMulti
                              placeholder={
                                isCodingProblemsLoading
                                  ? 'Loading coding problems...'
                                  : 'Select coding problems'
                              }
                              error={errors.codingProblems?.message}
                              className="w-full"
                              isLoading={isCodingProblemsLoading}
                            />
                          </div>
                          <div className="bg-green-100 border border-green-300 rounded-md p-3">
                            <p className="text-green-800 text-sm">
                              <strong>Fixed Assignment:</strong> All candidates
                              will receive the same set of coding problems.
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Random Assignment Configuration */}
                      {codingChallengeType === 'random' && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                            <h3 className="text-sm font-medium text-blue-900">
                              Random Assignment Configuration
                            </h3>
                          </div>
                          <p className="text-sm text-gray-700">
                            Configure random assignment of coding challenges.
                            Each candidate will receive a unique set of problems
                            based on your criteria.
                          </p>

                          {/* Query and Non-Query Counts */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Query-based Challenges *
                                <span className="text-xs text-gray-500 block font-normal">
                                  Problems marked as query-based
                                </span>
                              </label>
                              <InputField
                                type="number"
                                {...register('queryCount')}
                                placeholder="Number of query challenges"
                                error={errors.queryCount?.message}
                                min="0"
                                className="w-full"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Non-Query Challenges *
                                <span className="text-xs text-gray-500 block font-normal">
                                  Problems not marked as query-based
                                </span>
                              </label>
                              <InputField
                                type="number"
                                {...register('nonQueryCount')}
                                placeholder="Number of non-query challenges"
                                error={errors.nonQueryCount?.message}
                                min="0"
                                className="w-full"
                              />
                            </div>
                          </div>

                          {/* Calculated Total Display */}
                          {calculatedTotal > 0 && (
                            <div className="bg-blue-100 border border-blue-300 rounded-md p-3">
                              <div className="flex items-center gap-2">
                                <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                                <span className="text-sm font-medium text-blue-900">
                                  Total Challenges per Candidate:{' '}
                                  {calculatedTotal}
                                </span>
                              </div>
                            </div>
                          )}

                          {/* Problem Pool */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Problem Pool *
                              <span className="text-xs text-gray-500 block font-normal">
                                Select all problems that can be randomly
                                assigned to candidates
                              </span>
                            </label>
                            <SelectFieldHookForm
                              control={control}
                              name="problemPool"
                              options={codingOptions}
                              isMulti
                              placeholder={
                                isCodingProblemsLoading
                                  ? 'Loading coding problems...'
                                  : 'Select problems for random assignment pool'
                              }
                              error={errors.problemPool?.message}
                              className="w-full"
                              isLoading={isCodingProblemsLoading}
                            />
                          </div>

                          {/* Information Box */}
                          <div className="bg-blue-100 border border-blue-300 rounded-md p-3">
                            <h4 className="font-medium text-blue-900 mb-2 text-sm">
                              Important Notes:
                            </h4>
                            <ul className="text-blue-800 text-xs space-y-1">
                              <li>
                                • Total challenges are automatically calculated
                                from query + non-query counts
                              </li>
                              <li>
                                • Problem Pool should contain more problems than
                                Total Challenges for better randomization
                              </li>
                              <li>
                                • Each candidate will get unique random problems
                                from the pool
                              </li>
                            </ul>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Form Actions */}
                  <div className="flex items-center justify-end gap-4 pt-6 border-t border-gray-200">
                    <Button
                      type="button"
                      onClick={handleCancel}
                      variant="outlined"
                    >
                      Cancel
                    </Button>
                    <Button type="submit" loading={isLoading}>
                      {isEditMode ? 'Update Assessment' : 'Create Assessment'}
                    </Button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssessmentForm;
